import dagre from '@dagrejs/dagre';
import { useMemo } from 'react';
import { Node, Edge, MarkerType } from 'reactflow';

interface LineageEvent {
  metadata: {
    eventId: string;
    eventType: string;
    version?: number;
    timestamp: string;
    causedBy?: string | string[] | null;
    source?: string;
    sourceStore?: string;
  };
  storeName?: string;
  _storeName?: string;
  data?: unknown;
}

const STORE_PALETTE = ['#0972d3', '#037f0c', '#d97706', '#7c3aed'];

function getEventStoreName(event: LineageEvent): string | undefined {
  return event.storeName || event._storeName || event.metadata?.sourceStore || undefined;
}

export function buildStoreColorMap(events: LineageEvent[], currentStore?: string): Map<string, string> {
  const map = new Map<string, string>();
  const otherPalette = currentStore ? STORE_PALETTE.slice(1) : STORE_PALETTE;
  let idx = 0;
  for (const event of events) {
    const store = getEventStoreName(event);
    if (!store || map.has(store)) continue;
    if (store === currentStore) {
      map.set(store, STORE_PALETTE[0]);
    } else {
      map.set(store, otherPalette[idx % otherPalette.length]);
      idx++;
    }
  }
  return map;
}

export function useLineageLayout(events: LineageEvent[], currentStore?: string) {
  return useMemo(() => {
    if (!events || events.length === 0) return { nodes: [] as Node[], edges: [] as Edge[], storeColorMap: new Map<string, string>() };

    const storeColorMap = buildStoreColorMap(events, currentStore);

    const g = new dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph({ rankdir: 'TB', ranksep: 80, nodesep: 40 });

    const nodes: Node[] = events.map(event => {
      const meta = event.metadata;
      const isOrigin = !meta.causedBy;
      const isAI = (meta.source || '').includes('llm') || (meta.source || '').includes('bedrock');
      const eventStore = getEventStoreName(event);
      const color = eventStore ? (storeColorMap.get(eventStore) || STORE_PALETTE[0]) : getNodeColor(meta.causedBy, meta.source);

      g.setNode(meta.eventId, { width: 170, height: 78 });

      return {
        id: meta.eventId,
        type: 'eventNode',
        position: { x: 0, y: 0 },
        data: {
          eventType: meta.eventType,
          version: meta.version || 0,
          timestamp: meta.timestamp,
          causedBy: meta.causedBy,
          source: meta.source,
          storeName: eventStore,
          color,
          isOrigin,
          isAI,
          fullEvent: event,
        },
      };
    });

    const edges: Edge[] = [];
    const eventIds = new Set(events.map(e => e.metadata.eventId));

    for (const event of events) {
      const causedBy = event.metadata.causedBy;
      if (!causedBy) continue;

      const parents = Array.isArray(causedBy) ? causedBy : [causedBy];
      for (const parentId of parents) {
        if (eventIds.has(parentId)) {
          g.setEdge(parentId, event.metadata.eventId);

          const parentEvent = events.find(e => e.metadata.eventId === parentId);
          const elapsed = parentEvent
            ? getElapsed(parentEvent.metadata.timestamp, event.metadata.timestamp)
            : '';

          edges.push({
            id: `${parentId}-${event.metadata.eventId}`,
            source: parentId,
            target: event.metadata.eventId,
            label: elapsed,
            labelStyle: { fill: '#8d9daf', fontSize: '10px' },
            style: { stroke: '#539fe5', strokeWidth: 1.5 },
            markerEnd: { type: MarkerType.ArrowClosed, color: '#539fe5' },
          });
        }
      }
    }

    dagre.layout(g);

    for (const node of nodes) {
      const pos = g.node(node.id);
      if (pos) {
        node.position = { x: pos.x - 85, y: pos.y - 39 };
      }
    }

    return { nodes, edges, storeColorMap };
  }, [events, currentStore]);
}

function getNodeColor(causedBy?: string | string[] | null, source?: string): string {
  if (!causedBy) return '#0972d3';
  if (source && (source.includes('llm') || source.includes('bedrock'))) return '#6b5b95';
  return '#037f0c';
}

function getElapsed(from: string, to: string): string {
  try {
    const ms = new Date(to).getTime() - new Date(from).getTime();
    if (ms < 1000) return `+${ms}ms`;
    if (ms < 60000) return `+${(ms / 1000).toFixed(1)}s`;
    return `+${(ms / 60000).toFixed(1)}m`;
  } catch { return ''; }
}
